import React from 'react';
import { common } from '@kurtosys/ksys-app-template';
import { IComponentStyle } from '@kurtosys/types/componentStyle/IComponentStyle.js';
import { observer } from 'mobx-react';

import { ComponentBase } from '../../../shared/ComponentBase.js';
import { InjectedStyledComponent } from '../../../shared/InjectedStyledComponent.js';

import {
	IRelativePerformanceChartBenchmark,
	IRelativePerformanceChartBounds,
	IRelativePerformanceChartConfiguration,
	IRelativePerformanceChartProps,
	IRelativePerformanceChartSeries,
	IRelativePerformanceChartState,
} from './models/index.js';
import BenchmarkIndicator from './styledComponents/BenchmarkIndicator.js';
import BenchmarkIndicatorDot from './styledComponents/BenchmarkIndicatorDot.js';
import BenchmarkIndicatorLabel from './styledComponents/BenchmarkIndicatorLabel.js';
import BenchmarkIndicatorLine from './styledComponents/BenchmarkIndicatorLine.js';
import Block from './styledComponents/Block.js';
import BlockLabel from './styledComponents/BlockLabel.js';
import Blocks from './styledComponents/Blocks.js';
import BlocksWrapper from './styledComponents/BlocksWrapper.js';
import BlockValue from './styledComponents/BlockValue.js';
import BlockWrapper from './styledComponents/BlockWrapper.js';
import Chart from './styledComponents/Chart.js';
import Wrapper from './styledComponents/Wrapper.js';
import {
	RELATIVE_PERFORMANCE_CHART_CATEGORY_KEY,
	RELATIVE_PERFORMANCE_CHART_COMPONENT_KEY,
} from './RelativePerformanceChart.meta.js';

export class RelativePerformanceChart extends ComponentBase<
	IRelativePerformanceChartProps,
	IRelativePerformanceChartState,
	IRelativePerformanceChartConfiguration
> {
	categoryKey = RELATIVE_PERFORMANCE_CHART_CATEGORY_KEY;
	componentKey = RELATIVE_PERFORMANCE_CHART_COMPONENT_KEY;

	constructor(props: IRelativePerformanceChartProps) {
		super(props);
	}
	get series(): IRelativePerformanceChartSeries[] {
		const { series = [] } = this.props;
		const benchmark = this.benchmark;
		const benchmarkValue = benchmark.value || 1;
		return series
			.map((s) => {
				let relativeValue: number | undefined;
				if (!common.commonUtils.isNullOrUndefined(s.relativeValue)) {
					relativeValue = s.relativeValue;
				} else if (!common.commonUtils.isNullOrUndefined(s.value)) {
					relativeValue = s.value - benchmarkValue;
				}
				if (!common.commonUtils.isNullOrUndefined(relativeValue)) {
					relativeValue = parseFloat(relativeValue.toPrecision(8));
				}
				return {
					...s,
					relativeValue,
				};
			})
			.filter((s) => typeof s.relativeValue === 'number');
	}

	getBounds = (series: IRelativePerformanceChartSeries[]): IRelativePerformanceChartBounds => {
		const valueKey: keyof IRelativePerformanceChartSeries = 'relativeValue';
		const allNumberValues = series.every((s) => typeof s[valueKey] === 'number');
		let bounds: IRelativePerformanceChartBounds = {};
		if (allNumberValues) {
			bounds = series.reduce((acc: { max?: number; min?: number }, serie) => {
				const value = serie[valueKey];
				if (typeof value === 'number') {
					if (common.commonUtils.isUndefined(acc.max)) {
						acc.max = value;
					} else {
						acc.max = Math.max(value, acc.max);
					}
					if (common.commonUtils.isUndefined(acc.min)) {
						acc.min = value;
					} else {
						acc.min = Math.min(value, acc.min);
					}
				}
				return acc;
			}, {});
			bounds.count = series.length;

			// The benchmark will always be at 0 so we must have it on the range
			if (typeof bounds.max === 'number' && typeof bounds.min === 'number') {
				bounds.min = Math.min(bounds.min, 0);
				bounds.max = Math.max(bounds.max, 0);
				const { seriesValuePaddingPercent = 0 } = this.props;
				if (seriesValuePaddingPercent) {
					const originalRange = bounds.max - bounds.min;
					const paddingValue = originalRange * (seriesValuePaddingPercent / 100);
					if (paddingValue > 0) {
						bounds.max += paddingValue;
						bounds.min -= paddingValue;
					}
				}
				bounds.range = bounds.max - bounds.min;
			}
		}
		return bounds;
	};

	scale(value: number, bounds: IRelativePerformanceChartBounds): number {
		const offsetValue = value - (bounds.min || 0);
		return 100 - (offsetValue / (bounds.range || 1)) * 100;
	}

	get defaultBenchmark(): IRelativePerformanceChartBenchmark {
		return {
			value: 1,
			dots: 'both',
		};
	}

	get benchmark(): IRelativePerformanceChartBenchmark {
		const { benchmark } = this.props;
		return common.commonUtils.deepMergeObjects(this.defaultBenchmark, benchmark);
	}

	getSeriesColor = (value: number) => {
		const { colorRanges = [], defaultSeriesColor = 'blue' } = this.props;
		const colorRange = colorRanges.find((colorRange) => {
			const { start, end } = colorRange;
			return (
				(common.commonUtils.isNullOrUndefined(start) || value >= start) &&
				(common.commonUtils.isNullOrUndefined(end) || value < end)
			);
		});
		return (colorRange && colorRange.color) || defaultSeriesColor;
	};

	render() {
		const { className } = this.props;
		const series = this.series;

		if (!series || series.length === 0) {
			console.warn(
				'No series available to render the RelativePerformanceChart, relativeValue could not be determined or no series passed',
			);
			return null;
		}
		const bounds = this.getBounds(series);
		const benchmarkIndicatorComponentStyle: IComponentStyle = {
			top: `${this.scale(0, bounds)}%`,
		};
		const benchmark = this.benchmark;
		return (
			<Wrapper className={className}>
				<Chart>
					<BlocksWrapper>
						<BenchmarkIndicator componentStyle={benchmarkIndicatorComponentStyle}>
							{benchmark.label && <BenchmarkIndicatorLabel>{benchmark.label}</BenchmarkIndicatorLabel>}
							{benchmark.dots && ['left', 'both'].includes(benchmark.dots) && (
								<BenchmarkIndicatorDot componentStyle={{ left: '0' }} />
							)}
							<BenchmarkIndicatorLine />
							{benchmark.dots && ['right', 'both'].includes(benchmark.dots) && (
								<BenchmarkIndicatorDot componentStyle={{ right: '0' }} />
							)}
						</BenchmarkIndicator>
						<Blocks>
							{series.map((serie) => {
								const value = serie.relativeValue;
								if (common.commonUtils.isNullOrUndefined(value)) {
									return null;
								}
								const blockValueComponentStyle: IComponentStyle = {
									background: {
										color: this.getSeriesColor(value),
									},
									top: `${this.scale(value, bounds)}%`,
								};
								return (
									<BlockWrapper key={serie.key}>
										<Block>
											<BlockValue componentStyle={blockValueComponentStyle} />
										</Block>
									</BlockWrapper>
								);
							})}
						</Blocks>
					</BlocksWrapper>
					<Blocks>
						{series.map((serie) => {
							const value = serie.relativeValue;
							if (common.commonUtils.isNullOrUndefined(value)) {
								return null;
							}
							return (
								<BlockWrapper key={serie.key}>
									<BlockLabel>{serie.label || serie.key}</BlockLabel>
								</BlockWrapper>
							);
						})}
					</Blocks>
				</Chart>
			</Wrapper>
		);
	}
}

export default InjectedStyledComponent(
	observer(RelativePerformanceChart),
	RELATIVE_PERFORMANCE_CHART_CATEGORY_KEY,
	RELATIVE_PERFORMANCE_CHART_COMPONENT_KEY,
);
